import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Folder, Loader2, ArrowLeft, RefreshCw, AlertTriangle } from 'lucide-react';
import { getAuth } from 'firebase/auth';

const EXPORT_SERVICE_URL = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';

export const DeletePage: React.FC = () => {
    const [folders, setFolders] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);

    const fetchFolders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const user = getAuth().currentUser;
            if (!user) {
                setError('Vous devez être connecté pour accéder à cette page.');
                setLoading(false);
                return;
            }

            const res = await fetch(`${EXPORT_SERVICE_URL}/media/folders`);
            if (!res.ok) throw new Error(`Erreur serveur HTTP ${res.status}`);
            const data = await res.json();

            setFolders(data.folders || []);
        } catch (err: any) {
            setError(err.message || String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFolders();
    }, [fetchFolders]);

    const handleDeleteFolder = async (prefix: string) => {
        const confirm1 = window.confirm(`⚠️ ATTENTION ⚠️\n\nÊtes-vous sûr de vouloir supprimer DÉFINITIVEMENT le dossier "${prefix}" et TOUT son contenu ?\n\nCette action est irréversible.`);
        if (!confirm1) return;

        const confirm2 = window.prompt(`Pour confirmer la suppression de ce dossier, tapez exactement :\n\n${prefix}\n`);
        if (confirm2 !== prefix) {
            alert('Suppression annulée : le nom tapé ne correspond pas.');
            return;
        }

        setDeleting(prefix);
        try {
            const res = await fetch(`${EXPORT_SERVICE_URL}/media/delete-folder`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prefix }),
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Erreur HTTP ${res.status}`);
            }

            const data = await res.json();
            alert(`✅ Succès !\n${data.deletedCount} fichiers ont été supprimés du dossier "${prefix}".`);

            // Remove the deleted folder from the UI list
            setFolders(prev => prev.filter(f => f !== prefix));
        } catch (err: any) {
            alert(`Erreur lors de la suppression: ${err.message || err}`);
        } finally {
            setDeleting(null);
        }
    };

    // Styles
    const s = {
        page: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0a0f 0%, #12121f 50%, #0a0a0f 100%)',
            color: '#e5e7eb',
            fontFamily: "'Inter', -apple-system, sans-serif",
        },
        header: {
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(10,10,15,0.9)',
            backdropFilter: 'blur(20px)',
            position: 'sticky' as const,
            top: 0,
            zIndex: 50,
        },
        card: {
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 14,
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'all 0.2s',
        }
    };

    return (
        <div style={s.page}>
            {/* Header */}
            <div style={s.header}>
                <div style={{ maxWidth: 800, margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <a href="/media" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280', textDecoration: 'none', fontSize: 13, transition: 'color 0.2s' }}>
                            <ArrowLeft size={15} /> Médias
                        </a>
                        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Trash2 size={20} style={{ color: '#ef4444' }} />
                            <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#ef4444' }}>
                                Suppression de Dossiers
                            </h1>
                        </div>
                    </div>
                    <button onClick={fetchFolders} disabled={loading || deleting !== null} style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)',
                        color: '#d1d5db', cursor: 'pointer', fontSize: 12,
                    }}>
                        <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
                        Actualiser
                    </button>
                </div>
            </div>

            {/* Content */}
            <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px' }}>
                <div style={{
                    marginBottom: 30, padding: 16, borderRadius: 12,
                    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                    display: 'flex', gap: 12, alignItems: 'flex-start'
                }}>
                    <AlertTriangle size={24} style={{ color: '#ef4444', flexShrink: 0 }} />
                    <div>
                        <h3 style={{ margin: '0 0 6px', color: '#ef4444', fontSize: 15, fontWeight: 600 }}>Zone de Danger</h3>
                        <p style={{ margin: 0, color: '#fca5a5', fontSize: 13, lineHeight: 1.5 }}>
                            Cette page vous permet de supprimer des dossiers entiers de votre bucket Cloudflare R2 d'un seul clic.
                            La suppression est définitive et irréversible. Aucun système de corbeille n'est disponible.
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Loader2 size={32} style={{ color: '#ef4444', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                        <p style={{ marginTop: 12, color: '#9ca3af', fontSize: 14 }}>Chargement des dossiers...</p>
                    </div>
                ) : error ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <p style={{ color: '#ef4444', fontSize: 14 }}>{error}</p>
                        <button onClick={fetchFolders} style={{ marginTop: 16, padding: '8px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.2)', color: '#ef4444', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                            Réessayer
                        </button>
                    </div>
                ) : folders.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 0', color: '#6b7280' }}>
                        <Folder size={48} style={{ opacity: 0.2, margin: '0 auto 16px', display: 'block' }} />
                        <p style={{ fontSize: 14 }}>Aucun dossier trouvé dans votre bucket R2.</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {folders.map((folder) => {
                            const isDeleting = deleting === folder;
                            return (
                                <div key={folder} style={{
                                    ...s.card,
                                    borderColor: isDeleting ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.07)',
                                    opacity: deleting && !isDeleting ? 0.3 : 1
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                        <div style={{
                                            width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: 'rgba(255,255,255,0.05)', color: '#9ca3af'
                                        }}>
                                            <Folder size={20} />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#f3f4f6' }}>{folder}</h3>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => handleDeleteFolder(folder)}
                                        disabled={deleting !== null}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 8,
                                            border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)',
                                            color: '#ef4444', cursor: deleting !== null ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500,
                                            transition: 'all 0.2s',
                                        }}
                                        onMouseEnter={e => { if (deleting === null) e.currentTarget.style.background = 'rgba(239,68,68,0.2)' }}
                                        onMouseLeave={e => { if (deleting === null) e.currentTarget.style.background = 'rgba(239,68,68,0.1)' }}
                                    >
                                        {isDeleting ? (
                                            <>
                                                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                                Suppression...
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 size={16} />
                                                Supprimer
                                            </>
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default DeletePage;
